import { z } from 'zod';

export const EVIDENCE_BUNDLE_MAX_LOGS = 500;
export const EVIDENCE_BUNDLE_MAX_REQUEST_BYTES = 256 * 1024;
export const EVIDENCE_BUNDLE_MAX_PAYLOAD_BYTES = 5 * 1024 * 1024;
export const EVIDENCE_BUNDLE_SCHEMA_VERSION = 2;
export const EVIDENCE_BUNDLE_SIGNATURE_VERSION = 1;

const objectId = z.string().regex(/^[0-9a-fA-F]{24}$/, 'Invalid ID format');
const boundedBody = (schema) => schema.superRefine((value, ctx) => {
    if (new TextEncoder().encode(JSON.stringify(value)).length > EVIDENCE_BUNDLE_MAX_REQUEST_BYTES) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Request payload is too large' });
    }
});

/**
 * Authorization contract: View grants tenant-wide list/detail access and Export
 * grants tenant-wide create/seal/export access. IDs never confer authority; the
 * API always rebinds bundles and selected logs to the authenticated tenant.
 */
export const EvidenceBundleListQuerySchema = z.object({
    entityType: z.string().trim().min(1).max(120),
    entityId: objectId,
}).strict();

export const EvidenceBundleCreateRequestSchema = boundedBody(z.object({
    entityType: z.string().trim().min(1).max(120),
    entityId: objectId,
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(4000).optional().default(''),
    logIds: z.array(objectId).min(1).max(EVIDENCE_BUNDLE_MAX_LOGS)
        .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, 'Duplicate log IDs are not allowed'),
    frameworks: z.array(z.string().trim().min(1).max(80)).max(50).optional().default([]),
    customFields: z.record(z.unknown()).optional(),
}).strict());

export const EvidenceBundleExportRequestSchema = z.object({
    format: z.enum(['json', 'txt']).default('json'),
}).strict();

const ArtifactMetadataSchema = z.object({
    format: z.enum(['json', 'txt']),
    checksum: z.string().regex(/^[0-9a-f]{64}$/),
    size: z.number().int().nonnegative(),
    contentType: z.string(),
    storageReference: z.object({
        resourceId: z.string().min(1),
        versionId: z.string().min(1),
        etag: z.string().min(1),
    }),
    createdAt: z.union([z.string(), z.date()]),
    operationId: z.string().min(1),
}).passthrough();

export const EvidenceBundleResponseSchema = z.object({
    _id: z.any(),
    entityType: z.string(),
    entityId: z.any(),
    title: z.string(),
    description: z.string().optional(),
    logIds: z.array(z.any()),
    frameworks: z.array(z.string()),
    status: z.enum(['draft', 'sealed']),
    schemaVersion: z.number().int().positive(),
    signatureVersion: z.number().int().positive().nullable().optional(),
    signature: z.object({ algo: z.string(), value: z.string() }).optional(),
    exportArtifacts: z.array(z.union([ArtifactMetadataSchema, z.object({ format: z.enum(['json', 'txt']), checksum: z.string().optional() }).passthrough()])),
}).passthrough();

export const EvidenceBundleEnvelopeSchema = z.object({
    success: z.literal(true),
    data: EvidenceBundleResponseSchema,
});

export const EvidenceBundleListEnvelopeSchema = z.object({
    success: z.literal(true),
    data: z.array(EvidenceBundleResponseSchema).max(100),
});
